import { useNavbarHeightPx } from '@components/common/Navbar/Navbar.utils'
import { getBreakpointValue } from '@utils/responsiveness/breakpoints'
import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { isStickySectionShorterThanWindowHeight } from './StickyScroll.utils'
import { useScrollPosition } from '@utils/responsiveness/useScrollPosition'

export function StickyScroll({
  children,
  configurableSectionRef,
}: {
  children: ReactNode
  configurableSectionRef: React.MutableRefObject<HTMLElement>
}) {
  const [hasScrolled, setHasScrolled] = useState(false)

  useScrollPosition(
    ({ prevPos, currPos }) => {
      const scrolled = currPos.y < prevPos.y
      if (scrolled !== hasScrolled) {
        setHasScrolled(scrolled)
      }
    },
    [hasScrolled]
  )

  const {
    primaryMenuHeightPx,
    announcementBarHeightPx,
    secondaryMenuHeightPx,
  } = useNavbarHeightPx()
  const desktopNavHeight =
    primaryMenuHeightPx + secondaryMenuHeightPx + announcementBarHeightPx

  const stickySectionRef = useRef(null)
  const spacerDivRef = useRef(null)

  useEffect(() => {
    const handleWindowScroll = () => {
      // Don't do anything if in mobile/tablet
      if (window.innerWidth < getBreakpointValue('lg')) {
        stickySectionRef.current.style.top = `${desktopNavHeight}px`
        stickySectionRef.current.style.bottom = ''
        spacerDivRef.current.style.marginTop = '0px'
        return
      }

      const { height: stickySectionHeight, bottom: stickySectionBottom } =
        stickySectionRef?.current?.getBoundingClientRect() || {
          height: 0,
          bottom: 0,
        }

      if (
        isStickySectionShorterThanWindowHeight(
          stickySectionHeight,
          desktopNavHeight
        )
      ) {
        stickySectionRef.current.style.top = `${desktopNavHeight}px`
        stickySectionRef.current.style.bottom = ''
        spacerDivRef.current.style.marginTop = '0px'
        return
      }

      const topOffset = window.innerHeight - stickySectionHeight
      const bottomOffset =
        window.innerHeight - stickySectionHeight - desktopNavHeight

      const marginTopAsInt = parseInt(spacerDivRef.current.style.marginTop, 10)

      if (hasScrolled) {
        stickySectionRef.current.style.top = `${topOffset}px`
        stickySectionRef.current.style.bottom = ''

        if (
          window.innerHeight === stickySectionBottom &&
          configurableSectionRef.current.getBoundingClientRect().bottom >
            window.innerHeight
        ) {
          spacerDivRef.current.style.marginTop = `${
            window.scrollY +
            window.innerHeight -
            (stickySectionHeight + desktopNavHeight)
          }px`
        }
      } else {
        stickySectionRef.current.style.top = ''
        stickySectionRef.current.style.bottom = `${bottomOffset}px`

        if (marginTopAsInt > window.scrollY) {
          spacerDivRef.current.style.marginTop = `${window.scrollY}px`
        }
      }
    }
    window.addEventListener('scroll', handleWindowScroll)
    return () => {
      window.removeEventListener('scroll', handleWindowScroll)
    }
  }, [hasScrolled])

  // Adjust top margin when configurable section height changes
  const [configurableSectionHeight, setConfigurableSectionHeight] = useState(0)
  useEffect(() => {
    if (
      configurableSectionRef?.current?.offsetHeight !==
      configurableSectionHeight
    ) {
      const difference =
        configurableSectionRef?.current?.offsetHeight -
        configurableSectionHeight

      if (difference < 0) {
        const marginNumber = parseInt(spacerDivRef.current.style.marginTop, 10)
        const newMargin = Math.min(marginNumber - difference, 0)

        // Smooth transition with easing
        const startTime = Date.now()
        const duration = 500
        const startMargin = marginNumber
        const marginDiff = newMargin - startMargin

        const interval = setInterval(() => {
          const elapsed = Date.now() - startTime
          const progress = Math.min(elapsed / duration, 1)

          // Easing function (easeOutQuad)
          const easedProgress = 1 - (1 - progress) * (1 - progress)

          const currentMargin = startMargin + marginDiff * easedProgress
          spacerDivRef.current.style.marginTop = `${currentMargin}px`

          if (progress >= 1) {
            clearInterval(interval)
          }
        }, 16)
      }

      setConfigurableSectionHeight(
        configurableSectionRef?.current?.offsetHeight
      )
    }
  }, [configurableSectionRef?.current?.offsetHeight])

  return (
    <>
      {/* When scrolling up, the div forces the sticky component to stay where it is when the css top value toggles */}
      <div
        id="spacerDivForStickyScrolling"
        className="h-0"
        style={{ marginTop: '0px' }}
        ref={spacerDivRef}
      ></div>
      <div
        className="lg:border-l lg:sticky lg:border-l-core-grey-20"
        ref={stickySectionRef}
      >
        {children}
      </div>
    </>
  )
}
